import { Badge, ExceptionList, HorizontalStack, Layout, Page } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import {
  getInventoryStockById,
  getInventoryStockRawById,
  postInventoryStock,
} from '../../../services/inventoriesService';
import { BarcodeMajor, HorizontalDotsMinor, SectionMajor } from '@shopify/polaris-icons';
import ViewInventoryPrimaryActions from '../../UI/Inventories/ViewInventoryPrimaryActions';
import moment from 'moment';
import InventoryNumbers from '../../UI/Inventories/InventoryNumbers';
import AddStockCard from '../../UI/Inventories/AddStockCard';
import ProductsWrapper from '../../UI/Products/ProductsWrapper';
import ProductsWrapperBarcode from '../../UI/Products/ProductsWrapperBarcode';
import InventoryStockTable from '../../UI/Tables/InventoryStockTable';
import { useInventoryQuery } from '../../../customHooks/useInventoryQueries';

// Mapeo de estados → label/badge para el header. El Polaris Badge no admite
// cualquier color: estos `status` son los de Polaris 11.
const STATUS_BADGE = {
  DRAFT: { label: 'Borrador', status: 'new' },
  SCHEDULED: { label: 'Programado', status: 'info' },
  OPEN: { label: 'En curso', status: 'attention' },
  FINISHED: { label: 'Finalizado', status: 'warning' },
  CLOSED: { label: 'Cerrado', status: 'success' },
  CANCELLED: { label: 'Cancelado', status: 'critical' },
};

const modes = [
  { slug: 'manual', label: 'Manual', icon: SectionMajor, getStock: getInventoryStockById },
  { slug: 'barcode', label: 'Barcode', icon: BarcodeMajor, getStock: getInventoryStockRawById },
];

const Inventory = () => {
  const { depositId, inventoryId } = useParams();

  // Detalle del inventario vía React Query: cuando se ejecuta una acción
  // (START / REVIEW / CLOSE / CANCEL) la mutación de
  // useInventoryActionMutation invalida esta query y la UI se refresca sola.
  // Adiós a `loadInventory()` manual.
  const { data: inventory } = useInventoryQuery(depositId, inventoryId);

  const [mode, setMode] = useState(modes[0]);
  const [stock, setStock] = useState(null);

  const returnMatch = window.location.search.match(/\??r=(.+)&?/);
  const returnUrl =
    returnMatch && returnMatch.length > 1
      ? window.atob(returnMatch[1])
      : `/deposits/${depositId}/inventories`;

  // Recarga el stock al cambiar de modo o cuando el estado del inventario
  // cambia (p. ej. al cerrar, queremos ver los balances actualizados).
  useEffect(() => {
    setStock(null);
    getInventoryStock(1, 10, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, inventory?.status]);

  const getInventoryStock = (page = 1, limit = 10, refresh = false) =>
    mode.getStock(depositId, inventoryId, { page, limit }).then((r) => setStock({ ...r.data, new: refresh }));

  const handleChangeMode = (slug) => setMode(modes.find((m) => m.slug === slug));

  const handleStockAdded = (stockAdded) => {
    if (mode.slug === 'manual') {
      const families = stockAdded.stock.reduce((acc, item) => {
        if (acc.indexOf(item.family) === -1) acc.push(item.family);
        return acc;
      }, []);
      families.forEach((family) => {
        getInventoryStockById(inventory.deposit.id, inventory.id, { s: family }).then((rr) =>
          setStock({ ...rr.data, update: true }),
        );
      });
    }

    if (mode.slug === 'barcode') {
      mode.getStock(depositId, inventoryId, { page: 1, limit: 1 }).then((r) => {
        setStock({ ...r.data, update: true });
      });
    }
  };

  const handleDestroy = (ele) => {
    postInventoryStock(inventory.deposit.id, inventory.id, [ele]).then((r) => {
      handleStockAdded(r.data);
    });
  };

  const TableComponent = (args) => <InventoryStockTable {...args} inventory={inventory} />;

  if (!inventory) return null;

  const badge = STATUS_BADGE[inventory.status];

  return (
    <Page
      backAction={{ content: 'Volver', url: returnUrl }}
      title={inventory.name}
      titleMetadata={
        <HorizontalStack gap="3">
          {badge && <Badge status={badge.status}>{badge.label}</Badge>}
          {!inventory.canAddStock && (
            <ExceptionList
              items={[
                {
                  icon: HorizontalDotsMinor,
                  status: 'warning',
                  title: 'No se puede añadir mercancía',
                },
                ...(inventory.status === 'SCHEDULED'
                  ? [
                      {
                        icon: HorizontalDotsMinor,
                        status: 'info',
                        title: `Inventario programado para el ${moment(inventory.startDate).format('DD/MM/YYYY HH:mm')}`,
                      },
                    ]
                  : []),
                ...(inventory.status === 'CANCELLED'
                  ? [{ icon: HorizontalDotsMinor, status: 'critical', title: 'Inventario cancelado' }]
                  : []),
              ]}
            />
          )}
        </HorizontalStack>
      }
      subtitle={`${moment(inventory.createdAt).format('DD/MM/YYYY')} | ${inventory.deposit.name}`}
      primaryAction={
        <ViewInventoryPrimaryActions
          inventory={inventory}
          modes={modes}
          currentMode={mode}
          onChangeMode={handleChangeMode}
        />
      }
      divider
    >
      <Layout>
        <Layout.Section>
          <InventoryNumbers inventory={inventory} />
        </Layout.Section>
        {inventory.canAddStock && (
          <Layout.Section>
            <AddStockCard inventory={inventory} onStockAdded={handleStockAdded} />
          </Layout.Section>
        )}
        <Layout.Section>
          {stock && (
            <>
              {mode.slug === 'manual' && (
                <ProductsWrapper
                  data={stock}
                  TableComponent={TableComponent}
                  loadPage={() => getInventoryStock(stock.metadata.page + 1)}
                />
              )}
              {mode.slug === 'barcode' && (
                <ProductsWrapperBarcode
                  data={stock}
                  onDestroy={handleDestroy}
                  loadPage={() => getInventoryStock(stock.metadata.page + 1)}
                />
              )}
            </>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Inventory;
